import { Box, Button, InputAdornment, OutlinedInput, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { palette } from '@ahora-patagonia/design-tokens';
import { IconoBuscar } from '../components/iconos.js';
import { ListadoNotas } from '../notas/ListadoNotas.js';
import { useSincronizarPagina } from '../shell/paginaPublica.js';
import { useSsrData } from '../ssr/SsrDataProvider.js';
import { NOT_FOUND_ROUTE_KEY, type NotFoundData } from '../../notfound/notfound.loader.js';

/**
 * El 404 del sitio: dice lo que corresponde ("no existe o la nota se archivó"), explica que
 * las direcciones no cambian, y ofrece las tres salidas del handoff — portada, búsqueda y
 * últimas publicadas. El status 404 lo pone el server (rama sin match o loader sin entidad).
 */
export function NotFoundView({ data: dataProp }: { data?: NotFoundData | null }) {
  // El 404 llega por dos caminos: la rama sin match del server (key propia) o un loader
  // cuya entidad no existe (la vista de la ruta lo pasa por prop).
  const dataDeRuta = useSsrData<NotFoundData>(NOT_FOUND_ROUTE_KEY);
  const data = dataProp ?? dataDeRuta;
  const navigate = useNavigate();
  useSincronizarPagina(data);

  return (
    <Box sx={{ maxWidth: 720, py: 4 }}>
      <Typography variant="tituloNota" component="h1">
        Esta página no existe
      </Typography>
      <Typography variant="bajada" component="p" sx={{ mt: 1.5 }}>
        La dirección puede estar mal escrita, o la nota se archivó. Las direcciones de las
        notas publicadas no cambian nunca: si guardaste un link de este medio y ya no
        funciona, es porque la nota se despublicó.
      </Typography>

      <Box
        component="form"
        role="search"
        sx={{ display: 'flex', gap: 1, mt: 3 }}
        onSubmit={(e) => {
          e.preventDefault();
          const q = new FormData(e.currentTarget).get('q');
          navigate(q ? `/buscar?q=${encodeURIComponent(String(q))}` : '/buscar');
        }}
      >
        <OutlinedInput
          name="q"
          fullWidth
          placeholder="Buscar en el sitio"
          inputProps={{ 'aria-label': 'Buscar en el sitio' }}
          endAdornment={
            <InputAdornment position="end">
              <IconoBuscar />
            </InputAdornment>
          }
        />
        <Button type="submit" variant="contained">
          Buscar
        </Button>
      </Box>

      <Button component={RouterLink} to="/" variant="outlined" sx={{ mt: 2 }}>
        Ir a la portada
      </Button>

      {data && data.ultimas.length > 0 && (
        <Box component="section" sx={{ mt: 5 }}>
          <Typography
            variant="tituloBloque"
            component="h2"
            sx={{ borderBottom: `2px solid ${palette.ink}`, pb: 0.5, mb: 1 }}
          >
            Últimas publicadas
          </Typography>
          <ListadoNotas notas={data.ultimas} />
        </Box>
      )}
    </Box>
  );
}
